import { CommonModule } from '@angular/common';
import * as i1$1 from '@angular/common/http';
import * as i0 from '@angular/core';
import { Component, InjectionToken, NgModule, Inject, Optional } from '@angular/core';
import { WINDOW_TOKEN } from '@usana/ux/common';
import * as i2$1 from '@usana/ux/sso-auth';
import { ID_TOKEN_NAME, LOGIN_SERVICE_URL, LOGIN_REALM, SsoAuthModule } from '@usana/ux/sso-auth';
import * as i3 from '@usana/ux/universal-components/modal';
import { UniversalModalModule } from '@usana/ux/universal-components/modal';
import * as i1 from '@ng-bootstrap/ng-bootstrap';
import * as i2 from '@usana/ux/translations';
import { PackagePrimeMode, UsanaTranslationsModule } from '@usana/ux/translations';
import * as i4 from '@usana/ux/universal-components/button';
import { UniversalButtonModule } from '@usana/ux/universal-components/button';

class SessionChangedModalComponent {
    constructor(modal, translationConfig) {
        this.modal = modal;
        this.translationConfig = translationConfig;
    }
    ngOnInit() {
        this.translationConfig.addPackage({
            pkg: 'sso-session',
            primeMode: PackagePrimeMode.ON_FIRST_REQUEST,
            showTagWhenMissing: true,
            allowLooseMarketDefinition: true,
        });
    }
    onClose() {
        this.modal.close();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SessionChangedModalComponent, deps: [{ token: i1.NgbActiveModal }, { token: i2.TranslationConfig }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: SessionChangedModalComponent, isStandalone: true, selector: "universal-session-changed-modal", ngImport: i0, template: "<universal-modal-base>\n  <ng-container modalBody>\n    <div>{{ 'sessionChanged' | i18n : 'sso-session' }}</div>\n  </ng-container>\n  <ng-container modalFooter>\n    <universal-modal-buttons>\n      <universal-button (click)=\"onClose()\" [data]=\"{ type: 'tertiary', automationId: 'sso-session-close' }\">\n        {{ 'close' | i18n : 'sso-session' }}\n      </universal-button>\n    </universal-modal-buttons>\n  </ng-container>\n</universal-modal-base>\n", dependencies: [{ kind: "ngmodule", type: UniversalModalModule }, { kind: "component", type: i3.UniversalModalButtonsComponent, selector: "universal-modal-buttons", inputs: ["modalOptions"], outputs: ["buttonClicked"] }, { kind: "component", type: i3.UniversalModalBaseComponent, selector: "universal-modal-base", inputs: ["modalOptions", "removeXButton"] }, { kind: "ngmodule", type: UsanaTranslationsModule }, { kind: "pipe", type: i2.I18nPipe, name: "i18n" }, { kind: "ngmodule", type: UniversalButtonModule }, { kind: "component", type: i4.ButtonComponent, selector: "universal-button" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SessionChangedModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'universal-session-changed-modal', standalone: true, imports: [UniversalModalModule, UsanaTranslationsModule, UniversalButtonModule], template: "<universal-modal-base>\n  <ng-container modalBody>\n    <div>{{ 'sessionChanged' | i18n : 'sso-session' }}</div>\n  </ng-container>\n  <ng-container modalFooter>\n    <universal-modal-buttons>\n      <universal-button (click)=\"onClose()\" [data]=\"{ type: 'tertiary', automationId: 'sso-session-close' }\">\n        {{ 'close' | i18n : 'sso-session' }}\n      </universal-button>\n    </universal-modal-buttons>\n  </ng-container>\n</universal-modal-base>\n" }]
        }], ctorParameters: () => [{ type: i1.NgbActiveModal }, { type: i2.TranslationConfig }] });

const URL_ON_TOKEN_CHANGE = new InjectionToken('sso.tokenChange.url');
const BACKEND_LOADER_ON_INIT = new InjectionToken('sso.backendLoaderOnInit');
const DISABLE_SESSION_CHANGED_MODAL = new InjectionToken('sso.tokenChange.disable');

class SsoAuthBackendLoaderModule {
    constructor(httpClient, ssoAuthService, modalService, idTokenName, window, loginServiceUrl, loginRealm, urlOnTokenChange, loadOnInit, disableSessionChangedModal) {
        this.httpClient = httpClient;
        this.ssoAuthService = ssoAuthService;
        this.modalService = modalService;
        this.idTokenName = idTokenName;
        this.window = window;
        this.loginServiceUrl = loginServiceUrl;
        this.loginRealm = loginRealm;
        this.urlOnTokenChange = urlOnTokenChange;
        this.loadOnInit = loadOnInit;
        this.disableSessionChangedModal = disableSessionChangedModal;
        this.SESSION_CHANGED_MODAL_OPTIONS = {
            removeXButton: true,
            keyboard: false,
            backdrop: 'static',
            beforeDismiss: () => false,
        };
        if (!disableSessionChangedModal) {
            if (loadOnInit) {
                this.initUser();
            }
            this.window.onfocus = this.checkUserOnFocus.bind(this);
        }
    }
    loadUserFromBackend() {
        return this.httpClient.get(`${this.loginServiceUrl}/user/validate`, {
            withCredentials: true,
            observe: 'response',
            params: {
                headerResponse: '1',
                legacyTokenOrg: this.loginRealm,
            },
        });
    }
    checkUserOnFocus() {
        const currentUser = this.ssoAuthService.getUserUnchecked();
        if (this.modalIsOpen) {
            // if the modal is open or we have no user there is no reason to check if their stuff changed.
            return;
        }
        this.loadUserFromBackend().subscribe({
            next: (response) => {
                if (response.headers.has(this.idTokenName)) {
                    // we have a token
                    // the ticket does say explicitly we should reload the page so we should reach out and see if they are worried about the logged in user changing
                    // or actual details on the page
                    if (!this.ssoAuthService.equals(currentUser, this.ssoAuthService.parseIdTokenJwt(response.headers.get(this.idTokenName)))) {
                        this.modalIsOpen = true;
                        this.modalService
                            .openCustom(this.SESSION_CHANGED_MODAL_OPTIONS, SessionChangedModalComponent)
                            .closed.subscribe({
                            next: () => {
                                if (this.urlOnTokenChange) {
                                    this.window.location.replace(this.urlOnTokenChange);
                                }
                                else {
                                    this.window.location.reload();
                                }
                            },
                        });
                    }
                }
            },
        });
    }
    initUser() {
        this.loadUserFromBackend().subscribe({
            next: (response) => {
                if (!response.headers.has(this.idTokenName) ||
                    !this.ssoAuthService.setUserFromIdToken(response.headers.get(this.idTokenName))) {
                    this.ssoAuthService.initNoUser();
                }
            },
            error: (resp) => {
                // error so we don't have a user
                this.ssoAuthService.initNoUser();
            },
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SsoAuthBackendLoaderModule, deps: [{ token: i1$1.HttpClient }, { token: i2$1.SsoAuthService }, { token: i3.UniversalModalService }, { token: ID_TOKEN_NAME }, { token: WINDOW_TOKEN }, { token: LOGIN_SERVICE_URL }, { token: LOGIN_REALM }, { token: URL_ON_TOKEN_CHANGE }, { token: BACKEND_LOADER_ON_INIT, optional: true }, { token: DISABLE_SESSION_CHANGED_MODAL, optional: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: SsoAuthBackendLoaderModule, imports: [CommonModule, SsoAuthModule, UniversalModalModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SsoAuthBackendLoaderModule, imports: [CommonModule, SsoAuthModule, UniversalModalModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SsoAuthBackendLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SsoAuthModule, UniversalModalModule],
                }]
        }], ctorParameters: () => [{ type: i1$1.HttpClient }, { type: i2$1.SsoAuthService }, { type: i3.UniversalModalService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ID_TOKEN_NAME]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW_TOKEN]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGIN_SERVICE_URL]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGIN_REALM]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [URL_ON_TOKEN_CHANGE]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [BACKEND_LOADER_ON_INIT]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DISABLE_SESSION_CHANGED_MODAL]
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BACKEND_LOADER_ON_INIT, DISABLE_SESSION_CHANGED_MODAL, SessionChangedModalComponent, SsoAuthBackendLoaderModule, URL_ON_TOKEN_CHANGE };
